import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Tabs,
  Tab,
  Badge,
  IconButton,
  Fab,
  Button,
  useTheme,
  alpha
} from '@mui/material';
import {
  Chat as ChatIcon,
  StickyNote2 as NotesIcon,
  Notifications as NotificationsIcon,
  Add as AddIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Announcement as AnnouncementIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import ChatList from '../components/team/ChatList';
import ChatWindow from '../components/team/ChatWindow';
import NotesList from '../components/team/NotesList';
import NotificationsList from '../components/team/NotificationsList';
import CreateChatDialog from '../components/team/CreateChatDialog';
import CreateNoteDialog from '../components/team/CreateNoteDialog';
import { Chat, Note, TeamNotification } from '../utils/types';
import { getUserChats, subscribeToUserChats } from '../services/chatService';
import { getUserNotes, getSharedNotes, subscribeToUserNotes } from '../services/noteService';
import { getUserNotifications, subscribeToNotifications, getUnreadNotificationCount } from '../services/teamNotificationService';
// دوال بديلة
const addSampleUsers = async () => ({ success: true });
import { initializeTeamCommunication } from '../utils/firebaseInit';
const updateFirebaseRulesForTesting = async () => ({ success: true });
const testFirebasePermissions = async (userId: string) => ({ success: true, message: 'تم الاختبار بنجاح' });
import FirebaseRulesHelper from '../components/admin/FirebaseRulesHelper';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`team-tabpanel-${index}`}
      aria-labelledby={`team-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 0 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TeamCommunicationPage: React.FC = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [chats, setChats] = useState<Chat[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [notifications, setNotifications] = useState<TeamNotification[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [createChatOpen, setCreateChatOpen] = useState(false);
  const [createNoteOpen, setCreateNoteOpen] = useState(false);
  const [firebaseRulesHelperOpen, setFirebaseRulesHelperOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // تحميل البيانات الأولية
  useEffect(() => {
    if (!currentUser) return;

    const loadInitialData = async () => {
      try {
        setLoading(true);

        // تهيئة نظام التواصل الجماعي
        console.log('Initializing team communication system...');
        await initializeTeamCommunication();

        // تحديث قواعد Firebase للاختبار
        console.log('Updating Firebase rules for testing...');
        await updateFirebaseRulesForTesting();

        console.log('Team communication system initialized successfully');

        // تحميل المحادثات
        const userChats = await getUserChats(currentUser.id);
        setChats(userChats);

        // تحميل الملاحظات
        const [personalNotes, sharedNotes] = await Promise.all([
          getUserNotes(currentUser.id),
          getSharedNotes(currentUser.id)
        ]);
        setNotes([...personalNotes, ...sharedNotes]);

        // تحميل الإشعارات
        const userNotifications = await getUserNotifications(currentUser.id);
        setNotifications(userNotifications);

        // تحميل عدد الإشعارات غير المقروءة
        const count = await getUnreadNotificationCount(currentUser.id);
        setUnreadCount(count);

      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [currentUser]);

  // الاستماع للتحديثات المباشرة
  useEffect(() => {
    if (!currentUser) return;

    // الاستماع للمحادثات
    const unsubscribeChats = subscribeToUserChats(currentUser.id, (updatedChats) => {
      setChats(updatedChats);
    });

    // الاستماع للملاحظات
    const unsubscribeNotes = subscribeToUserNotes(currentUser.id, (updatedNotes) => {
      setNotes(prevNotes => {
        // دمج الملاحظات الشخصية مع المشتركة
        const sharedNotes = prevNotes.filter(note => note.authorId !== currentUser.id);
        return [...updatedNotes, ...sharedNotes];
      });
    });

    // الاستماع للإشعارات
    const unsubscribeNotifications = subscribeToNotifications(currentUser.id, (updatedNotifications) => {
      setNotifications(updatedNotifications);
      const unreadCount = updatedNotifications.filter(n => !n.isRead).length;
      setUnreadCount(unreadCount);
    });

    return () => {
      unsubscribeChats();
      unsubscribeNotes();
      unsubscribeNotifications();
    };
  }, [currentUser]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSelectedChat(null); // إغلاق نافذة المحادثة عند تغيير التبويب
  };

  const handleChatSelect = (chat: Chat) => {
    setSelectedChat(chat);
  };

  const handleCreateChat = () => {
    setCreateChatOpen(true);
  };

  const handleCreateNote = () => {
    setCreateNoteOpen(true);
  };

  const getTabIcon = (index: number) => {
    switch (index) {
      case 0:
        return <ChatIcon />;
      case 1:
        return <NotesIcon />;
      case 2:
        return (
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        );
      default:
        return <ChatIcon />;
    }
  };

  const getTabLabel = (index: number) => {
    switch (index) {
      case 0:
        return 'المحادثات';
      case 1:
        return 'الملاحظات';
      case 2:
        return 'الإشعارات';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Typography variant="h6" color="text.secondary">
            جاري تحميل نظام التواصل...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* العنوان الرئيسي */}
      <Box mb={4}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              🤝 نظام التواصل الجماعي
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              تواصل مع فريق العمل، شارك الملاحظات، وابق على اطلاع بآخر التحديثات
            </Typography>
          </Box>

          {/* أزرار الإعداد والاختبار */}
          <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={async () => {
                try {
                  await addSampleUsers();
                  alert('تم إضافة المستخدمين التجريبيين بنجاح!');
                  window.location.reload();
                } catch (error) {
                  console.error('خطأ في إضافة البيانات التجريبية:', error);
                  alert('حدث خطأ في إضافة البيانات التجريبية');
                }
              }}
            >
              إضافة بيانات تجريبية
            </Button>

            <Button
              variant="contained"
              size="small"
              color="warning"
              onClick={async () => {
                try {
                  console.log('🔧 تحديث قواعد Firebase...');
                  await updateFirebaseRulesForTesting();
                  alert('تم تحديث قواعد Firebase للاختبار!');
                } catch (error) {
                  console.error('خطأ في تحديث القواعد:', error);
                  alert('حدث خطأ في تحديث القواعد');
                }
              }}
            >
              تحديث قواعد Firebase
            </Button>

            <Button
              variant="contained"
              size="small"
              color="success"
              onClick={async () => {
                try {
                  console.log('🧪 اختبار الصلاحيات...');
                  const result = await testFirebasePermissions(currentUser?.id || '');
                  if (result.success) {
                    alert('✅ اختبار الصلاحيات نجح!');
                  } else {
                    alert(`❌ فشل اختبار الصلاحيات: ${result.message}`);
                  }
                } catch (error) {
                  console.error('خطأ في اختبار الصلاحيات:', error);
                  alert('حدث خطأ في اختبار الصلاحيات');
                }
              }}
            >
              اختبار الصلاحيات
            </Button>

            <Button
              variant="contained"
              size="small"
              color="error"
              onClick={() => setFirebaseRulesHelperOpen(true)}
            >
              🔥 إصلاح قواعد Firebase
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* الشريط الجانبي */}
        <Grid item xs={12} md={4} lg={3}>
          <Paper 
            elevation={2} 
            sx={{ 
              height: 'calc(100vh - 200px)',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            {/* التبويبات */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    minHeight: 64,
                    fontSize: '0.875rem',
                    fontWeight: 600
                  }
                }}
              >
                {[0, 1, 2].map((index) => (
                  <Tab
                    key={index}
                    icon={getTabIcon(index)}
                    label={getTabLabel(index)}
                    iconPosition="top"
                    sx={{
                      '&.Mui-selected': {
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }}
                  />
                ))}
              </Tabs>
            </Box>

            {/* محتوى التبويبات */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <TabPanel value={tabValue} index={0}>
                <ChatList
                  chats={chats}
                  selectedChat={selectedChat}
                  onChatSelect={handleChatSelect}
                  currentUserId={currentUser?.id || ''}
                />
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <NotesList
                  notes={notes}
                  currentUserId={currentUser?.id || ''}
                />
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <NotificationsList
                  notifications={notifications}
                  currentUserId={currentUser?.id || ''}
                />
              </TabPanel>
            </Box>
          </Paper>
        </Grid>

        {/* المحتوى الرئيسي */}
        <Grid item xs={12} md={8} lg={9}>
          {tabValue === 0 && selectedChat ? (
            <ChatWindow
              chat={selectedChat}
              currentUserId={currentUser?.id || ''}
              currentUserName={currentUser?.name || ''}
              currentUserRole={currentUser?.role}
            />
          ) : tabValue === 0 ? (
            <Paper 
              elevation={2} 
              sx={{ 
                height: 'calc(100vh - 200px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2
              }}
            >
              <Box textAlign="center">
                <ChatIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  اختر محادثة للبدء
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  أو أنشئ محادثة جديدة باستخدام الزر أدناه
                </Typography>
              </Box>
            </Paper>
          ) : (
            <Paper 
              elevation={2} 
              sx={{ 
                height: 'calc(100vh - 200px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2
              }}
            >
              <Box textAlign="center">
                {tabValue === 1 ? (
                  <>
                    <NotesIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      إدارة الملاحظات
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      أنشئ ملاحظات جديدة أو شاركها مع الفريق
                    </Typography>
                  </>
                ) : (
                  <>
                    <NotificationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      مركز الإشعارات
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      تابع جميع التحديثات والإشعارات المهمة
                    </Typography>
                  </>
                )}
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* أزرار الإجراءات السريعة */}
      <Box sx={{ position: 'fixed', bottom: 24, right: 24, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {tabValue === 0 && (
          <Fab
            color="primary"
            onClick={handleCreateChat}
            sx={{ mb: 1 }}
          >
            <ChatIcon />
          </Fab>
        )}
        
        {tabValue === 1 && (
          <Fab
            color="secondary"
            onClick={handleCreateNote}
          >
            <AddIcon />
          </Fab>
        )}
      </Box>

      {/* نوافذ الحوار */}
      <CreateChatDialog
        open={createChatOpen}
        onClose={() => setCreateChatOpen(false)}
        currentUserId={currentUser?.id || ''}
        currentUserName={currentUser?.name || ''}
        currentUserRole={currentUser?.role}
      />

      <CreateNoteDialog
        open={createNoteOpen}
        onClose={() => setCreateNoteOpen(false)}
        currentUserId={currentUser?.id || ''}
        currentUserName={currentUser?.name || ''}
      />

      <FirebaseRulesHelper
        open={firebaseRulesHelperOpen}
        onClose={() => setFirebaseRulesHelperOpen(false)}
      />
    </Container>
  );
};

export default TeamCommunicationPage;
